import { Box, IconButton, alpha } from "@mui/material";
import { Mic, MicOff, Square } from "lucide-react";

const BTN_SIZE = { xs: 36, sm: 40 };

const pillShellSx = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 1,
  background: "linear-gradient(to right, #22d3ee, #06b6d4, #3b82f6)",
  borderRadius: "9999px",
  px: { xs: 1, sm: 1.5 },
  py: { xs: 0.75, sm: 1 },
  boxShadow: "0 12px 28px -8px rgba(59, 130, 246, 0.55)",
};

const circleBtnBase = {
  width: BTN_SIZE,
  height: BTN_SIZE,
  flexShrink: 0,
  borderRadius: "50%",
  boxShadow:
    "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
  transition: "all 0.2s ease",
  "&:hover": {
    boxShadow:
      "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
  },
  "&:active": {
    transform: "scale(0.95)",
  },
  "&.Mui-disabled": {
    opacity: 0.55,
  },
};

/**
 * Voice-mode controls below the avatar.
 * Listening status is shown by VoiceModeVisual — no separate listening indicator here.
 *
 * Recording / muted: [mic] [stop]
 * Between turns (not muted): [start mic]
 */
export default function VoiceModeControls({
  isRecording = false,
  isMicMuted = false,
  isLoading = false,
  onToggleMicMute,
  onStopRecording,
  onStartVoiceMode,
}) {
  const showSessionControls = isRecording || isMicMuted;

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        justifyContent: "center",
        mt: 0,
        flexShrink: 0,
      }}
    >
      <Box sx={pillShellSx}>
        {showSessionControls ? (
          <>
            <IconButton
              onClick={onToggleMicMute}
              disabled={isLoading}
              sx={{
                ...circleBtnBase,
                bgcolor: isMicMuted ? "white" : "#d1fae5",
                color: isMicMuted ? "#6b7280" : "#059669",
                border: isMicMuted
                  ? `1px solid ${alpha("#9ca3af", 0.35)}`
                  : "none",
              }}
              aria-label={isMicMuted ? "Unmute microphone" : "Mute microphone"}
              title={isMicMuted ? "Unmute and start listening" : "Mute microphone"}
            >
              {isMicMuted ? (
                <MicOff style={{ width: 16, height: 16 }} />
              ) : (
                <Mic style={{ width: 16, height: 16 }} />
              )}
            </IconButton>

            <IconButton
              onClick={onStopRecording}
              disabled={isLoading}
              sx={{
                ...circleBtnBase,
                bgcolor: "#ef4444",
                color: "white",
                border: `3px solid ${alpha("#fecaca", 0.85)}`,
                "&:hover": { bgcolor: "#dc2626" },
                "&.Mui-disabled": {
                  bgcolor: alpha("#ef4444", 0.55),
                },
              }}
              aria-label="End voice conversation"
              title="End voice conversation"
            >
              <Square style={{ width: 16, height: 16, fill: "currentColor" }} />
            </IconButton>
          </>
        ) : (
          <IconButton
            onClick={onStartVoiceMode}
            disabled={isLoading}
            sx={{
              ...circleBtnBase,
              bgcolor: "#d1fae5",
              color: "#059669",
            }}
            aria-label="Start speaking"
            title="Start speaking"
          >
            <Mic style={{ width: 16, height: 16 }} />
          </IconButton>
        )}
      </Box>
    </Box>
  );
}
